import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { formatCurrency, getMonthName } from '../lib/payrollEngine';
import type { Payroll } from '../lib/supabase';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

export function ReportsPage() {
  const { company } = useAuth();
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => { if (company) load(); }, [company, selectedYear]);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('payrolls')
      .select('*')
      .eq('company_id', company!.id)
      .eq('period_year', selectedYear)
      .in('status', ['approved', 'paid'])
      .order('period_month');
    setPayrolls(data || []);
    setLoading(false);
  }

  const chartData = Array.from({ length: 12 }, (_, i) => {
    const p = payrolls.find(x => x.period_month === i + 1);
    return {
      month: getMonthName(i + 1).slice(0, 3),
      'Neto Total': p ? p.total_net : 0,
      'Bruto': p ? p.total_gross : 0,
      'EsSalud': p ? p.total_essalud : 0,
    };
  });

  const totalNet = payrolls.reduce((s, p) => s + p.total_net, 0);
  const totalGross = payrolls.reduce((s, p) => s + p.total_gross, 0);
  const totalEssalud = payrolls.reduce((s, p) => s + p.total_essalud, 0);
  const totalDeductions = payrolls.reduce((s, p) => s + p.total_deductions, 0);
  const avgNet = payrolls.length > 0 ? totalNet / payrolls.length : 0;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">Reportes</h1>
          <p className="text-on-surface-variant mt-1">Análisis del costo de planilla y tendencias</p>
        </div>
        <select
          className="input-base w-auto"
          value={selectedYear}
          onChange={e => setSelectedYear(+e.target.value)}
        >
          {[new Date().getFullYear(), new Date().getFullYear() - 1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {[
          { label: 'Total Bruto Anual', value: formatCurrency(totalGross), icon: 'account_balance_wallet', color: 'text-on-surface' },
          { label: 'Total Neto Anual', value: formatCurrency(totalNet), icon: 'payments', color: 'text-primary' },
          { label: 'EsSalud Empleador', value: formatCurrency(totalEssalud), icon: 'local_hospital', color: 'text-secondary' },
          { label: 'Promedio Neto/Mes', value: formatCurrency(avgNet), icon: 'trending_up', color: 'text-green-600' },
        ].map(k => (
          <div key={k.label} className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{k.label}</span>
              <span className={`material-symbols-outlined ${k.color} text-[20px]`}>{k.icon}</span>
            </div>
            <p className={`text-xl font-black tabular-nums ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="card p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-xl font-bold text-on-surface font-headline">Evolución Mensual de Planillas</h3>
            <p className="text-sm text-on-surface-variant">Comparativa neto, bruto y EsSalud — {selectedYear}</p>
          </div>
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} barGap={2} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e3e5" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#737685', fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#737685', fontFamily: 'Inter' }} axisLine={false} tickLine={false} tickFormatter={v => v > 0 ? `S/${(v/1000).toFixed(0)}k` : '0'} />
              <Tooltip
                formatter={(value: any, name) => [formatCurrency(Number(value)), name]}
                contentStyle={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '8px', fontSize: '12px' }}
              />
              <Legend wrapperStyle={{ fontSize: '12px', fontFamily: 'Inter' }} />
              <Bar dataKey="Bruto" fill="#b2c5ff" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Neto Total" fill="#003d9b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="EsSalud" fill="#545f73" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-outline-variant/10 flex items-center justify-between">
          <h3 className="font-bold text-on-surface font-headline">Resumen por período</h3>
          <span className="text-xs text-on-surface-variant">{payrolls.length} planillas procesadas en {selectedYear}</span>
        </div>

        {payrolls.length === 0 ? (
          <div className="py-12 text-center text-on-surface-variant text-sm">
            No hay planillas procesadas en {selectedYear}
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-surface-container-low">
                {['Período', 'Total Bruto', 'Deducciones', 'EsSalud', 'Neto a Pagar', 'Estado'].map(h => (
                  <th key={h} className="px-6 py-3 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {payrolls.map(p => (
                <tr key={p.id} className="hover:bg-surface-container-low/30 transition-colors">
                  <td className="px-6 py-4 text-sm font-bold text-on-surface">{getMonthName(p.period_month)} {p.period_year}</td>
                  <td className="px-6 py-4 text-sm tabular-nums font-medium">{formatCurrency(p.total_gross)}</td>
                  <td className="px-6 py-4 text-sm tabular-nums text-error">({formatCurrency(p.total_deductions)})</td>
                  <td className="px-6 py-4 text-sm tabular-nums text-on-surface-variant">{formatCurrency(p.total_essalud)}</td>
                  <td className="px-6 py-4 text-sm font-bold tabular-nums text-primary">{formatCurrency(p.total_net)}</td>
                  <td className="px-6 py-4">
                    <span className={p.status === 'paid' ? 'badge-success' : 'badge-neutral'}>
                      {p.status === 'paid' ? 'Pagado' : 'Aprobado'}
                    </span>
                  </td>
                </tr>
              ))}
              {/* Totals */}
              <tr className="bg-primary/5 font-bold">
                <td className="px-6 py-4 text-sm font-black text-on-surface">TOTALES {selectedYear}</td>
                <td className="px-6 py-4 text-sm tabular-nums">{formatCurrency(totalGross)}</td>
                <td className="px-6 py-4 text-sm tabular-nums text-error">({formatCurrency(totalDeductions)})</td>
                <td className="px-6 py-4 text-sm tabular-nums">{formatCurrency(totalEssalud)}</td>
                <td className="px-6 py-4 text-sm font-black tabular-nums text-primary">{formatCurrency(totalNet)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
